<template>
  <div class="offers q-my-lg q-mx-md q-mx-md-none">
    <!--
      Единый Carousel с адаптивными настройками
      Рендерится на сервере и клиенте одинаково
      Изображения переключаются через CSS
    -->
    <Carousel
        name="banner-offers"
        v-bind="carouselOptions"
        @slide-start="handleSlideStart"
        @slide-end="handleSlideEnd"
        @drag="handleDrag"
    >
      <CarouselSlide
          v-for="(offer, index) in offersActiveBanner"
          :key="offer.ID"
          @click="onSlideClick(offer)"
          class="c-slide"
      >
        <!-- Desktop изображение (скрыто на мобильных) -->
        <div
            v-if="offer.BANNER_HOR_DESKTOP?.SRC"
            class="c-slide__inner c-slide__inner--desktop gt-sm"
        >
          <img
              :src="imageResolve(offer.BANNER_HOR_DESKTOP.SRC)"
              :alt="offer.NAME || 'Баннер акции'"
              class="c-slide__img"
              :fetchpriority="index === 0 ? 'high' : 'auto'"
              :loading="index === 0 ? 'eager' : 'lazy'"
          />
        </div>

        <!-- Mobile изображение (скрыто на десктопе) -->
        <div
            v-if="offer.BANNER_HOR_MOBILE?.SRC"
            class="c-slide__inner c-slide__inner--mobile lt-md"
        >
          <img
              :src="imageResolve(offer.BANNER_HOR_MOBILE.SRC, 'm-1')"
              :alt="offer.NAME || 'Баннер акции'"
              class="c-slide__img"
              :fetchpriority="index === 0 ? 'high' : 'auto'"
              :loading="index === 0 ? 'eager' : 'lazy'"
          />
        </div>
      </CarouselSlide>

      <template #addons>
        <!-- Navigation только на desktop -->
        <CarouselNavigation class="gt-sm" />
        <CarouselPagination />
      </template>
    </Carousel>
  </div>
</template>

<script setup lang="ts">
import { useImager } from "@/core/store/imager";
import { Offer } from "@/gql/gen";
import { OfferModel, useOfferStore } from "@/modules/offer/store/offer";
import { useTimeoutFn, useWindowSize } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { useQuasar } from "quasar";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { CarouselConfig } from "vue3-carousel";

const $q = useQuasar();
const router = useRouter();
const offerStore = useOfferStore();
const {offersActiveBanner} = storeToRefs(offerStore);
const windowSize = useWindowSize();
const {imageResolve} = useImager();

// Единые адаптивные настройки карусели
// Используют breakpoints для разных экранов
const carouselOptions = computed<Partial<CarouselConfig>>(() => {
  const width = windowSize.width.value;

  // Mobile (< 600px)
  if (width < 600) {
    return {
      itemsToShow: 1,
      itemsToScroll: 1,
      wrapAround: false,
      snapAlign: 'center',
      gap: 20,
      autoplay: 4000,
    };
  }

  // Tablet (600-800px)
  if (width < 800) {
    return {
      itemsToShow: 1.4,
      itemsToScroll: 1,
      wrapAround: true,
      snapAlign: 'center',
      gap: 20,
      preventExcessiveDragging: false,
      autoplay: 4000,
    };
  }

  // Desktop (>= 800px)
  return {
    itemsToShow: 1.9,
    itemsToScroll: 1,
    wrapAround: true,
    snapAlign: 'center',
    gap: 20,
    preventExcessiveDragging: false,
    autoplay: 4000,
  };
});

const justSlideEnd = ref(false);
const isSliding = ref(false);
const currentOffer = ref<OfferModel>();

const openSlide = () => {
  if (currentOffer.value) {
    router.push('/offer/' + currentOffer.value.ID);
  }
};

const openSlideTimeout = useTimeoutFn(openSlide, 100);

const onSlideClick = (item: Offer) => {
  if (justSlideEnd.value || isSliding.value) {
    return;
  }
  currentOffer.value = item;
  openSlideTimeout.start();
};

const handleSlideStart = () => {
  // Можно добавить логику
};

const handleSlideEnd = () => {
  justSlideEnd.value = true;
  isSliding.value = false;
  setTimeout(() => {
    justSlideEnd.value = false;
  }, 100);
};

const handleDrag = (e: any) => {
  if (Math.abs(e.deltaX) > 5) {
    isSliding.value = true;
    openSlideTimeout.stop();
  }
};
</script>

<style lang="scss" scoped>
.c-slide {
  cursor: pointer;
  position: relative;

  &:not(.carousel__slide--active) {
    transform: scale(1, 0.9);
  }
}

// Базовые стили для обоих вариантов
.c-slide__inner {
  width: 100%;
  border-radius: 15px;
  overflow: hidden;

  // ВАЖНО: Предотвращаем layout shift
  // Резервируем место под изображение
  position: relative;
}

.c-slide__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

// Desktop изображение
.c-slide__inner--desktop {
  height: 20vw;
  min-height: 200px;

  // Скрыто на мобильных через Quasar класс .gt-sm
  // display: none на мобильных, display: block на desktop
}

// Mobile изображение
.c-slide__inner--mobile {
  height: 40vw;

  // Скрыто на desktop через Quasar класс .lt-md
  // display: block на мобильных, display: none на desktop
}

.offers {
  :deep(.carousel__pagination) {
    bottom: -18px;
  }

  :deep(.carousel__icon) {
    border: 1px solid #FFFFFF;
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    background-color: #FFFFFF88;
  }
}

@media (min-width: $breakpoint-md-min) {
  :deep(.carousel__next),
  :deep(.carousel__prev) {
    height: 100% !important;
  }

  :deep(.carousel__next) {
    padding-left: 5vw;
  }

  :deep(.carousel__prev) {
    padding-right: 5vw;
  }
}
</style>
